import { Fragment, useState, type ReactNode } from 'react';
import { invalidateDeviceCatalog } from '@/features/device-models/keys';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  Truck,
  ChevronDown,
  ChevronRight,
  Box,
  CheckCircle2,
  Loader2,
  Ban,
  XCircle,
  Plus,
} from '@/lib/icons';

import {
  getShipments,
  receiveShipment,
  cancelShipment,
  rejectShipmentItem,
} from '@/features/inventory/api';
import type {
  Shipment,
  ShipmentStatus,
  ShipmentItem,
} from '@/features/inventory/device-unit';
import { NewTransferDialog } from '@/features/inventory/components/NewTransferDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FilterChips, NoFilterResults } from '@/components/filters';
import type { FilterChip } from '@/components/filters';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { useToast } from '@/hooks/use-toast';
import { useAppSelector } from '@/store/hooks';
import { hasPermission } from '@/lib/auth/permissions';
import { canPerformDeviceAction } from '@/lib/auth/roles';
import { apiErrorMessage, isTimeoutError } from '@/lib/api';
import type { OperationResult } from '@/lib/operation-result';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/dates';

const SHIPMENT_STATUS_VALUES = ['dispatched', 'received', 'cancelled'] as const;

const transfersSearchSchema = z.object({
  status: z.enum(SHIPMENT_STATUS_VALUES).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_layout/inventory/transfers/')({
  validateSearch: transfersSearchSchema,
  component: TransfersPage,
});

const PAGE_SIZE = 20;

const SHIPMENT_BADGE: Record<string, string> = {
  dispatched:
    'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
  received:
    'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
  cancelled:
    'bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400',
};

const ITEM_BADGE: Record<string, string> = {
  in_transit:
    'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
  received:
    'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
  rejected: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
};

/**
 * Collects a free-text reason before a destructive shipment action.
 *
 * Replaces `window.prompt`, which rendered outside the design system, could
 * not be translated, accepted an empty string as a valid reason and — on a
 * cancel — is the only audit trail the shipment keeps.
 */
function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  pendingLabel,
  pending,
  onConfirm,
}: Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  pendingLabel: string;
  pending: boolean;
  onConfirm: (reason: string) => void;
}>) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  const close = (next: boolean) => {
    if (!next && pending) return;
    if (!next) setReason('');
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div>
          <label htmlFor="reason" className="text-sm font-medium mb-1.5 block">
            {t('inventory.transfers.reasonLabel')}
          </label>
          <Textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('inventory.transfers.reasonPlaceholder')}
            rows={3}
            maxLength={500}
            disabled={pending}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => close(false)}
            disabled={pending}
          >
            {t('common.actions.cancel')}
          </Button>
          <PendingButton
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => onConfirm(reason.trim())}
            disabled={reason.trim().length === 0}
            busy={pending}
            busyLabel={pendingLabel}
          >
            {confirmLabel}
          </PendingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A button that shows a spinner and a progress label while *its* action
 * runs. Busy stays at full opacity — only the siblings it locks are dimmed —
 * so the operator can see which of several identical buttons is working.
 */
function PendingButton({
  busy,
  busyLabel,
  icon,
  children,
  disabled,
  className,
  ...props
}: Readonly<
  ButtonProps & { busy: boolean; busyLabel: string; icon?: ReactNode }
>) {
  return (
    <Button
      {...props}
      disabled={disabled || busy}
      aria-busy={busy}
      className={cn(busy && 'cursor-wait disabled:opacity-100', className)}
    >
      {busy ? (
        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        icon
      )}
      {busy ? busyLabel : children}
    </Button>
  );
}

/** Which control started a shipment action — drives the per-button loader. */
type ShipmentAction =
  | 'receiveAll'
  | 'receiveRemaining'
  | 'cancel'
  | `receive:${string}`
  | `reject:${string}`;

type ShipmentActionVars = Readonly<{
  shipmentId: string;
  action: ShipmentAction;
}>;

// Every shipment mutation is keyed under this prefix, so the pending set can be
// read across all of them, and across concurrent actions on different shipments.
const SHIPMENT_ACTION_KEY = ['shipment-action'] as const;

type ReasonTarget =
  | { kind: 'cancelShipment'; shipmentId: string }
  | { kind: 'rejectItem'; shipmentId: string; item: ShipmentItem };

function TransfersPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const user = useAppSelector((s) => s.auth.user);

  // Receive / reject / cancel are all `inventory:update` on the backend
  // (transfer_router.py). Without this the buttons render for a read-only
  // agent and every click returns a 403 the page then has to explain.
  const canAct = hasPermission(user, 'inventory', 'update');
  // Dispatch is a different grant — `device_units:transfer`, per
  // `create_shipment` — so it is checked separately from the receive actions.
  const canDispatch = canPerformDeviceAction(user, 'transfer');

  const [expandedRows, setExpandedRows] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [reasonTarget, setReasonTarget] = useState<ReasonTarget | null>(null);
  // Remounting the dialog per opening is what resets it; see its docblock.
  const [newTransfer, setNewTransfer] = useState<{
    open: boolean;
    key: number;
  }>({ open: false, key: 0 });

  const openNewTransfer = () =>
    setNewTransfer((prev) => ({ open: true, key: prev.key + 1 }));

  const page = search.page ?? 1;
  const statusFilter = search.status ?? 'all';

  const setFilters = (next: Partial<z.infer<typeof transfersSearchSchema>>) =>
    navigate({
      to: '/inventory/transfers',
      search: (prev) => ({ ...prev, ...next }),
    });

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['shipments', search.status, page],
    queryFn: () =>
      getShipments({
        status: search.status as ShipmentStatus | undefined,
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
    placeholderData: (previous) => previous,
  });

  const shipments = data?.value ?? [];
  // `total` is the unpaginated count the list endpoint reports alongside the
  // page. Falling back to the page's own length keeps the control honest
  // against an older service that doesn't send it — it just means the last
  // page can't be known in advance.
  const total =
    typeof data?.metadata?.total === 'number'
      ? data.metadata.total
      : (page - 1) * PAGE_SIZE + shipments.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startItem = total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const endItem = Math.min(page * PAGE_SIZE, total);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['shipments'] });
    invalidateDeviceCatalog(queryClient);
    queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
  };

  // Every action returns the updated shipment. Writing it straight into the
  // cached lists flips the row the moment the loader stops, instead of leaving
  // stale buttons on screen until the background refetch lands.
  const applyShipment = (updated: Shipment | undefined) => {
    if (updated) {
      queryClient.setQueriesData<OperationResult<Shipment[]>>(
        { queryKey: ['shipments'] },
        (old) =>
          Array.isArray(old?.value)
            ? {
                ...old,
                value: old.value.map((s) =>
                  s.id === updated.id ? updated : s
                ),
              }
            : old
      );
    }
    invalidate();
  };

  const pendingActions = useMutationState({
    filters: { mutationKey: SHIPMENT_ACTION_KEY, status: 'pending' },
    select: (m) => m.state.variables as ShipmentActionVars,
  });
  // One action at a time per shipment: two receives racing on the same lines
  // could each decide the other left the shipment open.
  const busyByShipment = new Map(
    pendingActions.map((v) => [v.shipmentId, v.action])
  );
  const isBusy = (shipmentId: string, action: ShipmentAction) =>
    busyByShipment.get(shipmentId) === action;
  const isLocked = (shipmentId: string) => busyByShipment.has(shipmentId);

  const onMutationError = (e: unknown) => {
    if (isTimeoutError(e)) {
      // The change may have committed after the client stopped waiting; show
      // the real state rather than an error for something that happened.
      invalidate();
      toast({
        title: t('inventory.transfers.toast.slowTitle'),
        description: t('inventory.transfers.toast.slow'),
      });
      return;
    }
    toast({
      title: t('common.error'),
      description: apiErrorMessage(e),
      variant: 'destructive',
    });
  };

  // `itemIds` omitted receives the whole shipment; passing a subset receives
  // only those lines. The backend has always supported both — the page used
  // to send neither and silently received everything.
  const receiveMutation = useMutation({
    mutationKey: [...SHIPMENT_ACTION_KEY, 'receive'],
    mutationFn: ({
      shipmentId,
      itemIds,
    }: ShipmentActionVars & { itemIds?: string[] }) =>
      receiveShipment(shipmentId, itemIds ? { itemIds } : undefined),
    onSuccess: (result, vars) => {
      applyShipment(result.value);
      toast({
        title: t('common.success'),
        description: vars.itemIds
          ? t('inventory.transfers.toast.itemReceived', {
              count: vars.itemIds.length,
            })
          : t('inventory.toast.transferReceived'),
      });
    },
    onError: onMutationError,
  });

  const cancelMutation = useMutation({
    mutationKey: [...SHIPMENT_ACTION_KEY, 'cancel'],
    mutationFn: ({
      shipmentId,
      reason,
    }: ShipmentActionVars & { reason: string }) =>
      cancelShipment(shipmentId, reason),
    onSuccess: (result) => {
      applyShipment(result.value);
      setReasonTarget(null);
      toast({
        title: t('common.success'),
        description: t('inventory.transfers.toast.cancelled'),
      });
    },
    onError: onMutationError,
  });

  const rejectItemMutation = useMutation({
    mutationKey: [...SHIPMENT_ACTION_KEY, 'reject'],
    mutationFn: ({
      shipmentId,
      itemId,
      reason,
    }: ShipmentActionVars & { itemId: string; reason: string }) =>
      rejectShipmentItem(shipmentId, itemId, reason),
    onSuccess: (result) => {
      applyShipment(result.value);
      setReasonTarget(null);
      toast({
        title: t('common.success'),
        description: t('inventory.transfers.toast.itemRejected'),
      });
    },
    onError: onMutationError,
  });

  const toggleRow = (id: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const chips: ReadonlyArray<
    FilterChip<'all' | (typeof SHIPMENT_STATUS_VALUES)[number]>
  > = [
    { value: 'all', label: t('common.allStatuses') },
    ...SHIPMENT_STATUS_VALUES.map((value) => ({
      value,
      label: t(`inventory.transfers.status.${value}`),
    })),
  ];

  const reasonPending =
    cancelMutation.isPending || rejectItemMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('inventory.transfers.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('inventory.transfers.description')}
          </p>
        </div>
        {/* In the header, not only in the empty state: the empty state stops
            rendering the moment a first transfer exists, which left the page
            with no way to start a second one. */}
        {canDispatch && (
          <Button
            className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200"
            onClick={openNewTransfer}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('inventory.transfers.newTransfer')}
          </Button>
        )}
      </div>

      {/* Chips only — the list endpoint takes `status` and nothing else, so a
          search field or branch facet here would be dead UI. */}
      <FilterChips
        items={chips}
        value={statusFilter}
        onChange={(value) =>
          setFilters({
            status: value === 'all' ? undefined : value,
            page: undefined,
          })
        }
        ariaLabel={t('filters.status.label')}
        isPending={isFetching}
      />

      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Truck className="h-5 w-5" />
            {t('inventory.transfers.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6">
              <ApiErrorCard
                error={error}
                onRetry={() => refetch()}
                title={t('inventory.transfers.loadFailed')}
              />
            </div>
          ) : shipments.length === 0 ? (
            <NoFilterResults
              isFiltered={Boolean(search.status)}
              emptyTitle={t('inventory.transfers.empty.title')}
              emptyDescription={t('inventory.transfers.empty.description')}
              icon={Truck}
              onReset={() => setFilters({ status: undefined, page: undefined })}
              action={
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {canDispatch && (
                    <Button onClick={openNewTransfer}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t('inventory.transfers.newTransfer')}
                    </Button>
                  )}
                  {/* The units list stays reachable: dispatching a basket
                      picked there is still the faster route when the operator
                      is already looking at the handsets. */}
                  <Button
                    variant="ghost"
                    onClick={() => navigate({ to: '/inventory/units' })}
                  >
                    <Box className="w-4 h-4 mr-2" />
                    {t('inventory.transfers.empty.action')}
                  </Button>
                </div>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="w-8" />
                    <TableHead>
                      {t('inventory.transfers.columns.from')}
                    </TableHead>
                    <TableHead>{t('inventory.transfers.columns.to')}</TableHead>
                    <TableHead>
                      {t('inventory.transfers.columns.units')}
                    </TableHead>
                    <TableHead>
                      {t('inventory.transfers.columns.status')}
                    </TableHead>
                    <TableHead>
                      {t('inventory.transfers.columns.transferDate')}
                    </TableHead>
                    <TableHead>
                      {t('inventory.transfers.columns.dispatchedBy')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('common.actionsTitle')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shipments.map((shipment) => {
                    const expanded = expandedRows.has(shipment.id);
                    const inFlight = shipment.status === 'dispatched';
                    const pendingItems = shipment.items.filter(
                      (i) => i.status === 'in_transit'
                    );
                    return (
                      <Fragment key={shipment.id}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/30"
                          onClick={() => toggleRow(shipment.id)}
                        >
                          <TableCell>
                            {expanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {shipment.fromBranchName ??
                              t('inventory.transfers.externalSource')}
                          </TableCell>
                          <TableCell className="font-medium">
                            {shipment.toBranchName ?? '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {t('inventory.transfers.unitCount', {
                                count: shipment.itemCount,
                              })}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={SHIPMENT_BADGE[shipment.status] ?? ''}
                            >
                              {t(
                                `inventory.transfers.status.${shipment.status}`
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(shipment.dispatchedAt)}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {shipment.dispatchedByName ?? '-'}
                          </TableCell>
                          <TableCell
                            className="text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {canAct && inFlight && (
                              <div className="flex justify-end gap-1">
                                <PendingButton
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    receiveMutation.mutate({
                                      shipmentId: shipment.id,
                                      action: 'receiveAll',
                                    })
                                  }
                                  busy={isBusy(shipment.id, 'receiveAll')}
                                  busyLabel={t(
                                    'inventory.transfers.receivingAll'
                                  )}
                                  disabled={isLocked(shipment.id)}
                                  icon={
                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                  }
                                >
                                  {t('inventory.transfers.receiveAll')}
                                </PendingButton>
                                <PendingButton
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() =>
                                    setReasonTarget({
                                      kind: 'cancelShipment',
                                      shipmentId: shipment.id,
                                    })
                                  }
                                  busy={isBusy(shipment.id, 'cancel')}
                                  busyLabel={t(
                                    'inventory.transfers.cancelling'
                                  )}
                                  disabled={isLocked(shipment.id)}
                                  icon={<Ban className="w-3.5 h-3.5 mr-1.5" />}
                                >
                                  {t('inventory.transfers.cancel')}
                                </PendingButton>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>

                        {expanded && (
                          <TableRow className="bg-muted/10 hover:bg-muted/10">
                            {/* One full-width cell rather than aligning line
                                items to the header's columns — a shipment line
                                describes a handset, not a shipment, so the
                                columns never matched. */}
                            <TableCell colSpan={8} className="p-0">
                              <div className="border-l-2 border-l-primary/40 px-4 py-3 space-y-3">
                                {shipment.notes && (
                                  <p className="text-xs text-muted-foreground">
                                    <span className="font-medium">
                                      {t('inventory.transfers.notes')}:
                                    </span>{' '}
                                    {shipment.notes}
                                  </p>
                                )}
                                {shipment.status === 'cancelled' &&
                                  shipment.cancelReason && (
                                    <p className="text-xs text-muted-foreground">
                                      <span className="font-medium">
                                        {t('inventory.transfers.cancelReason')}:
                                      </span>{' '}
                                      {shipment.cancelReason}
                                      {shipment.cancelledByName &&
                                        ` — ${shipment.cancelledByName}`}
                                    </p>
                                  )}
                                {shipment.status === 'received' &&
                                  shipment.receivedByName && (
                                    <p className="text-xs text-muted-foreground">
                                      <span className="font-medium">
                                        {t(
                                          'inventory.transfers.columns.receivedBy'
                                        )}
                                        :
                                      </span>{' '}
                                      {shipment.receivedByName}
                                    </p>
                                  )}

                                {canAct && pendingItems.length > 1 && (
                                  <PendingButton
                                    size="sm"
                                    variant="outline"
                                    className="h-7"
                                    onClick={() =>
                                      receiveMutation.mutate({
                                        shipmentId: shipment.id,
                                        action: 'receiveRemaining',
                                        itemIds: pendingItems.map((i) => i.id),
                                      })
                                    }
                                    busy={isBusy(
                                      shipment.id,
                                      'receiveRemaining'
                                    )}
                                    busyLabel={t(
                                      'inventory.transfers.receivingRemaining',
                                      { count: pendingItems.length }
                                    )}
                                    disabled={isLocked(shipment.id)}
                                    icon={
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                                    }
                                  >
                                    {t('inventory.transfers.receiveRemaining', {
                                      count: pendingItems.length,
                                    })}
                                  </PendingButton>
                                )}

                                <ul className="divide-y rounded-md border bg-card">
                                  {shipment.items.map((item) => (
                                    <li
                                      key={item.id}
                                      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2"
                                    >
                                      <span className="font-mono text-xs text-muted-foreground min-w-[8rem]">
                                        {item.deviceImei ?? '-'}
                                      </span>
                                      <span className="text-sm flex-1 min-w-[8rem]">
                                        {[item.deviceBrand, item.deviceModel]
                                          .filter(Boolean)
                                          .join(' ') || '-'}
                                      </span>
                                      <Badge
                                        className={
                                          ITEM_BADGE[item.status] ?? ''
                                        }
                                      >
                                        {t(
                                          `inventory.transfers.itemStatus.${item.status}`
                                        )}
                                      </Badge>
                                      {item.status === 'rejected' &&
                                        item.rejectReason && (
                                          <span className="text-xs text-muted-foreground italic">
                                            {item.rejectReason}
                                          </span>
                                        )}
                                      {item.status === 'received' &&
                                        item.receivedAt && (
                                          <span className="text-xs text-muted-foreground">
                                            {formatDate(item.receivedAt)}
                                          </span>
                                        )}
                                      {canAct &&
                                        inFlight &&
                                        item.status === 'in_transit' && (
                                          <div className="flex gap-1 ml-auto">
                                            <PendingButton
                                              size="sm"
                                              variant="outline"
                                              className="h-7"
                                              onClick={() =>
                                                receiveMutation.mutate({
                                                  shipmentId: shipment.id,
                                                  action: `receive:${item.id}`,
                                                  itemIds: [item.id],
                                                })
                                              }
                                              busy={isBusy(
                                                shipment.id,
                                                `receive:${item.id}`
                                              )}
                                              busyLabel={t(
                                                'inventory.transfers.receiving'
                                              )}
                                              disabled={isLocked(shipment.id)}
                                            >
                                              {t('inventory.transfers.receive')}
                                            </PendingButton>
                                            <PendingButton
                                              size="sm"
                                              variant="ghost"
                                              className="h-7 text-destructive hover:text-destructive"
                                              onClick={() =>
                                                setReasonTarget({
                                                  kind: 'rejectItem',
                                                  shipmentId: shipment.id,
                                                  item,
                                                })
                                              }
                                              busy={isBusy(
                                                shipment.id,
                                                `reject:${item.id}`
                                              )}
                                              busyLabel={t(
                                                'inventory.transfers.rejecting'
                                              )}
                                              disabled={isLocked(shipment.id)}
                                              icon={
                                                <XCircle className="w-3.5 h-3.5 mr-1.5" />
                                              }
                                            >
                                              {t('inventory.transfers.reject')}
                                            </PendingButton>
                                          </div>
                                        )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {t('inventory.transfers.showingRange', {
              start: startItem,
              end: endItem,
              total,
            })}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setFilters({ page: Math.max(1, page - 1) })}
                  className={
                    page <= 1
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
              {Array.from({ length: totalPages }).map((_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    isActive={page === i + 1}
                    onClick={() => setFilters({ page: i + 1 })}
                    className="cursor-pointer"
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    setFilters({ page: Math.min(totalPages, page + 1) })
                  }
                  className={
                    page >= totalPages
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <ReasonDialog
        // Remounting per target clears the textarea between openings without
        // the parent having to own the draft.
        key={
          reasonTarget
            ? `${reasonTarget.kind}-${
                reasonTarget.kind === 'rejectItem'
                  ? reasonTarget.item.id
                  : reasonTarget.shipmentId
              }`
            : 'idle'
        }
        open={reasonTarget !== null}
        onOpenChange={(next) => !next && setReasonTarget(null)}
        pending={reasonPending}
        title={
          reasonTarget?.kind === 'rejectItem'
            ? t('inventory.transfers.rejectTitle')
            : t('inventory.transfers.cancelTitle')
        }
        description={
          reasonTarget?.kind === 'rejectItem'
            ? t('inventory.transfers.rejectDescription', {
                imei: reasonTarget.item.deviceImei ?? '',
              })
            : t('inventory.transfers.cancelDescription')
        }
        confirmLabel={
          reasonTarget?.kind === 'rejectItem'
            ? t('inventory.transfers.reject')
            : t('inventory.transfers.cancel')
        }
        pendingLabel={
          reasonTarget?.kind === 'rejectItem'
            ? t('inventory.transfers.rejecting')
            : t('inventory.transfers.cancelling')
        }
        onConfirm={(reason) => {
          if (!reasonTarget) return;
          if (reasonTarget.kind === 'rejectItem') {
            rejectItemMutation.mutate({
              shipmentId: reasonTarget.shipmentId,
              action: `reject:${reasonTarget.item.id}`,
              itemId: reasonTarget.item.id,
              reason,
            });
          } else {
            cancelMutation.mutate({
              shipmentId: reasonTarget.shipmentId,
              action: 'cancel',
              reason,
            });
          }
        }}
      />

      <NewTransferDialog
        key={newTransfer.key}
        open={newTransfer.open}
        onOpenChange={(open) => setNewTransfer((prev) => ({ ...prev, open }))}
        defaultFromBranchId={user?.branchId}
        onTransferred={() =>
          // A fresh shipment is `dispatched` and sorts to the top, so any other
          // status filter would hide the thing that was just created and read
          // as a dispatch that silently failed.
          setFilters({ status: undefined, page: undefined })
        }
      />
    </div>
  );
}
